import React, { type ReactElement, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import useAddToCart from './useAddToCart';

import { ravenCaptureException } from '../../helpers/raven';
import { shopifyAddToCartRequest } from '../../helpers/shopifyApiData';

import {
    aiAssistantProductRecommendationClicked,
    widgetActivityTracking,
} from '../../store/actions';
import { trackingEvents } from '../../store/activityTrackingHelpers';
import { AiAssistantProduct, AiAssistantProductVariant } from '../../store/typings';
import BottomSheetPortal from '../BottomSheet/BottomSheetPortal';
import { AddToCartButtonView } from './AddToCartButton';
import VariantPickerContent from './VariantPickerContent';

type AiAssistantAddToCartButtonProps = {
    product: AiAssistantProduct;
    variant: AiAssistantProductVariant;
    messageId: string;
};

const AiAssistantAddToCartButton = ({
    product,
    variant,
    messageId,
}: AiAssistantAddToCartButtonProps): ReactElement | null => {
    const dispatch = useDispatch();
    const { variant_id, currency } = variant;
    const [isVariantPickerOpen, setIsVariantPickerOpen] = useState(false);
    const onAddToCart = useCallback(
        async (selectedVariantId: number): Promise<boolean> => {
            dispatch(aiAssistantProductRecommendationClicked({ productId: product.product_id }));
            dispatch(widgetActivityTracking(trackingEvents.aiAssistantProductCardAddToCartClicked));

            try {
                const response = await shopifyAddToCartRequest(selectedVariantId);
                if (!response.ok) {
                    ravenCaptureException('Shopify add to cart request failed', {
                        message: response.statusText,
                    });
                    return false;
                }
                return true;
            } catch (error) {
                if (error instanceof Error) {
                    ravenCaptureException('Shopify add to cart request failed', {
                        message: error.message,
                    });
                }
                return false;
            }
        },
        [dispatch, product.product_id],
    );

    const onViewCartClick = useCallback(() => {
        dispatch(widgetActivityTracking(trackingEvents.aiAssistantProductCardViewCartClicked));
    }, [dispatch]);

    const { buttonState, executeAddToCart, handleViewCart } = useAddToCart({
        displayedVariantId: variant_id,
        messageId,
        onAddToCart,
        onViewCartClick,
    });

    const pickableVariants = useMemo(
        () =>
            product.variants.filter(
                (productVariant) => Object.keys(productVariant.variant).length > 0,
            ),
        [product.variants],
    );

    const handleAddToCartClick = useCallback((): void => {
        if (pickableVariants.length > 1) {
            setIsVariantPickerOpen(true);
        } else {
            executeAddToCart(variant_id);
        }
    }, [pickableVariants.length, variant_id, executeAddToCart]);

    const handleVariantSelect = useCallback(
        (selectedVariantId: number): void => {
            dispatch(
                widgetActivityTracking(
                    trackingEvents.aiAssistantProductCardVariantPickerVariantClicked,
                ),
            );
            setIsVariantPickerOpen(false);
            executeAddToCart(selectedVariantId);
        },
        [executeAddToCart, dispatch],
    );

    const closeVariantPicker = useCallback(() => {
        setIsVariantPickerOpen(false);
    }, []);

    return (
        <>
            <BottomSheetPortal isOpen={isVariantPickerOpen} onClose={closeVariantPicker}>
                <VariantPickerContent
                    variants={pickableVariants}
                    title={product.title}
                    currency={currency}
                    onClose={closeVariantPicker}
                    onSelect={handleVariantSelect}
                />
            </BottomSheetPortal>

            <AddToCartButtonView
                buttonState={buttonState}
                onAddToCartClick={handleAddToCartClick}
                onViewCartClick={handleViewCart}
            />
        </>
    );
};

export default AiAssistantAddToCartButton;
